import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerTopHeader from '../components/ManagerTopHeader';
import { API_BASE } from '../../config/runtime';

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const BellIcon = ({ color = 'currentColor' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8">
    <path d="M15 17H5.5a1.5 1.5 0 0 1-1.2-2.4l1.2-1.6V10a6.5 6.5 0 1 1 13 0v3l1.2 1.6A1.5 1.5 0 0 1 18.5 17H15" />
    <path d="M9 20a3 3 0 0 0 6 0" />
  </svg>
);

const EmptyIcon = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.6">
    <path d="M15 17H5.5a1.5 1.5 0 0 1-1.2-2.4l1.2-1.6V10a6.5 6.5 0 1 1 13 0v3l1.2 1.6A1.5 1.5 0 0 1 18.5 17H15" />
    <path d="M9 20a3 3 0 0 0 6 0" />
  </svg>
);

const getTypeTone = (type) => {
  const normalized = String(type || '').toLowerCase();
  if (normalized === 'success') return { dot: '#16a34a', pillBg: '#dcfce7', pillText: '#166534', cardBg: '#f0fdf4', border: '#bbf7d0' };
  if (normalized === 'warning') return { dot: '#ea580c', pillBg: '#ffedd5', pillText: '#9a3412', cardBg: '#fff7ed', border: '#fed7aa' };
  return { dot: '#2563eb', pillBg: '#dbeafe', pillText: '#1d4ed8', cardBg: '#eff6ff', border: '#bfdbfe' };
};

const formatAbsoluteDate = (value) => {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-PH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ManagerNotifications = () => {
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const userId = user?.user_id;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = async () => {
    if (!userId) {
      setLoading(false);
      setError('No logged-in manager found.');
      return;
    }

    try {
      setError('');
      const response = await fetch(`${API_BASE}/get_notifications.php?user_id=${encodeURIComponent(String(userId))}&limit=50`);
      const data = await response.json();
      if (!data.success) {
        setError(data.message || 'Failed to load notifications.');
        return;
      }
      setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
      setUnreadCount(Number(data.unread_count || 0));
    } catch {
      setError('Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const intervalId = window.setInterval(fetchNotifications, 30000);
    return () => window.clearInterval(intervalId);
  }, [userId]);

  const markAsRead = async (notificationId) => {
    if (!userId) return;

    setNotifications((prev) => prev.map((item) => (
      item.notification_id === notificationId ? { ...item, is_read: 1 } : item
    )));
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      const response = await fetch(`${API_BASE}/mark_notification_read.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_id: notificationId, user_id: userId }),
      });
      const data = await response.json();
      if (!data.success) fetchNotifications();
    } catch {
      fetchNotifications();
    }
  };

  const markAllAsRead = async () => {
    if (!userId || unreadCount === 0) return;
    setMarkingAll(true);
    try {
      const response = await fetch(`${API_BASE}/mark_notification_read.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, mark_all: true }),
      });
      const data = await response.json();
      if (data.success) {
        setNotifications((prev) => prev.map((item) => ({ ...item, is_read: 1 })));
        setUnreadCount(0);
      } else {
        fetchNotifications();
      }
    } catch {
      fetchNotifications();
    } finally {
      setMarkingAll(false);
    }
  };

  const handleNotificationClick = (notification) => {
    if (Number(notification.is_read) === 0) {
      markAsRead(notification.notification_id);
    }

    if (notification.booking_id) {
      navigate(`/manager/booking-request?highlight=${encodeURIComponent(String(notification.booking_id))}`);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <ManagerSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <ManagerTopHeader pageTitle="Notifications" />

        <main className="flex-1 overflow-auto">
          <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
            <section
              className="rounded-[24px] p-6 sm:p-8 text-white overflow-hidden relative"
              style={{ background: 'linear-gradient(135deg, #1c1310 0%, #7e4f46 52%, #b37b65 100%)' }}
            >
              <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                  <p className="m-0 text-[11px] font-extrabold uppercase tracking-[0.22em] text-white/65">
                    Manager Alerts
                  </p>
                  <h1 className="m-0 mt-2 text-2xl sm:text-3xl font-black">
                    Booking activity notifications
                  </h1>
                  <p className="m-0 mt-3 text-sm text-white/75 max-w-2xl">
                    New client bookings, booking updates, and client cancellations will appear here for your branch.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="px-4 py-2 rounded-full bg-white/10 border border-white/15 text-sm font-semibold">
                    {unreadCount} unread
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center backdrop-blur-sm">
                    <BellIcon color="#ffffff" />
                  </div>
                </div>
              </div>
              <div className="absolute -right-12 -bottom-12 w-40 h-40 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
            </section>

            <section className="mt-6 bg-white border border-slate-200 rounded-[22px] shadow-[0_8px_30px_rgba(15,23,42,0.05)] overflow-hidden">
              <div className="px-5 sm:px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="m-0 text-lg font-extrabold text-slate-900">Recent notifications</h2>
                  <p className="m-0 mt-1 text-sm text-slate-500">
                    Manager alerts are refreshed every 30 seconds.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={fetchNotifications}
                    className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-bold hover:bg-slate-200 transition"
                  >
                    Refresh
                  </button>
                  <button
                    onClick={markAllAsRead}
                    disabled={markingAll || unreadCount === 0}
                    className="px-4 py-2 rounded-xl bg-[#1c1310] text-white text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {markingAll ? 'Marking...' : 'Mark all read'}
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="p-6 sm:p-8 space-y-4">
                  {[...Array(3)].map((_, index) => (
                    <div key={index} className="h-28 rounded-[18px] bg-slate-100 animate-pulse" />
                  ))}
                </div>
              ) : error ? (
                <div className="p-6 sm:p-8">
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-rose-700 text-sm font-semibold">
                    {error}
                  </div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-6 py-14 text-center">
                  <div className="mx-auto w-16 h-16 rounded-3xl bg-slate-100 flex items-center justify-center">
                    <EmptyIcon />
                  </div>
                  <h3 className="m-0 mt-4 text-lg font-extrabold text-slate-900">No notifications yet</h3>
                  <p className="m-0 mt-2 text-sm text-slate-500">
                    Manager booking alerts will show here as clients book, edit, or cancel appointments.
                  </p>
                </div>
              ) : (
                <div className="p-4 sm:p-5 space-y-4">
                  {notifications.map((notification) => {
                    const tone = getTypeTone(notification.type);
                    const isUnread = Number(notification.is_read) === 0;

                    return (
                      <article
                        key={notification.notification_id}
                        className="rounded-[20px] border p-5 transition"
                        onClick={() => handleNotificationClick(notification)}
                        style={{
                          background: isUnread ? tone.cardBg : '#ffffff',
                          borderColor: isUnread ? tone.border : '#e2e8f0',
                          boxShadow: isUnread ? '0 10px 32px rgba(37,99,235,0.08)' : 'none',
                          cursor: notification.booking_id ? 'pointer' : 'default',
                        }}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                          <div className="flex items-start gap-4 min-w-0">
                            <div
                              className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                              style={{ background: tone.pillBg }}
                            >
                              <BellIcon color={tone.dot} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="m-0 text-base font-extrabold text-slate-900">
                                  {notification.title || 'Notification'}
                                </h3>
                                {isUnread && (
                                  <span className="inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.14em]" style={{ background: tone.pillBg, color: tone.pillText }}>
                                    Unread
                                  </span>
                                )}
                              </div>
                              <p className="m-0 mt-2 text-sm text-slate-600 leading-6">
                                {notification.message || 'No details available.'}
                              </p>
                              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                                <span>{notification.time_ago || formatAbsoluteDate(notification.created_at)}</span>
                                <span>{formatAbsoluteDate(notification.created_at)}</span>
                              </div>
                            </div>
                          </div>

                          {isUnread && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.notification_id);
                              }}
                              className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50 transition"
                            >
                              Mark as read
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ManagerNotifications;
